import { ref } from 'vue';
import {
  createCookie,
  deleteCookie,
  getCookie,
  nonEscapeHTMLArrayByObject,
  nonEscapeHTMLObject,
} from '~/composables/useCommon';
import { showAlert } from '~/composables/useConfirmDialog';
import { appT } from '~/composables/useAppI18n';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi';
import { useCommonApi } from '~/composables/useCommonApi';
import { useNoticeBoardApi } from './useNoticeBoardApi';

const { fetchPopNoticeList, fetchPopNoticeDetail } = useNoticeBoardApi();
const { fetchCheckPopNotice } = useCommonApi();
const { fetchAtchFileList } = useAttachmentApi();

const isNoticeModalOpen = ref(false);
const isNoticeDetailVisible = ref(false);
const noticeList = ref<any[]>([]);
const noticeDetail = ref<any>(null);
const noticeAttachmentFiles = ref<any[]>([]);
/** 로그인 후 공지 팝업 노출 여부 조회 결과 */
const popNoticeCheckResult = ref<any>(null);
/** 동일 세션에서 중복 호출 방지 */
const hasCheckedPopNotice = ref(false);
/** 로그인 팝업용 공지 목록 (AS-IS: gPopNoticeList — id, width, height) */
const loginPopNoticeList = ref<{ id: string; width: string; height: string }[]>([]);
/** 로그인 팝업용 공지 상세 목록 (selectDetail 조회 결과) */
const loginPopNoticeDetails = ref<any[]>([]);

const noticeFind = ref({
  id: '',
  colName: 'subject',
  searchKeyword: '',
  page: 1,
  rows: 1000,
});

const buildNoticeListParams = () => ({
  id: noticeFind.value.id,
  colName: noticeFind.value.colName,
  searchKeyword: noticeFind.value.searchKeyword,
  page: noticeFind.value.page,
  rows: noticeFind.value.rows,
});

const buildNoticeDetailParams = (id: string) => ({
  id,
  colName: noticeFind.value.colName,
  searchKeyword: noticeFind.value.searchKeyword,
});

const closeNoticeDetail = () => {
  isNoticeDetailVisible.value = false;
  noticeDetail.value = null;
  noticeAttachmentFiles.value = [];
  noticeFind.value.id = '';
};

const handleSelectNoticeList = async () => {
  try {
    closeNoticeDetail();
    const res = await fetchPopNoticeList(buildNoticeListParams());
    const rows = Array.isArray(res?.rows) ? res.rows : [];
    noticeList.value = nonEscapeHTMLArrayByObject(rows);
  } catch (e) {
    console.error(e);
    showAlert(appT('common.error'), appT('common.noticeBoard.listFetchError'));
  }
};

const handleNoticeSearch = async () => {
  await handleSelectNoticeList();
};

const popNotice = async () => {
  isNoticeModalOpen.value = true;
  await handleSelectNoticeList();
};

/** AS-IS: cPopNoticeClose + id */
const getLoginPopNoticeCookieName = (noticeId: string) => `cPopNoticeClose${noticeId}`;

const isLoginPopNoticeHidden = (noticeId: string) =>
  getCookie(getLoginPopNoticeCookieName(noticeId)) === 'Y';

/** 동시에 열린 로그인 공지 팝업들 */
const openLoginPopNoticeItems = ref<
  {
    id: string;
    notice: any;
    atchFileKey: string;
    detailUrl: string;
    attachmentFiles: any[];
    open: boolean;
    doNotShowAgain: boolean;
    zIndex: number;
    offsetIndex: number;
  }[]
>([]);

/** AS-IS: cookie != Y 인 건을 모두 동시에 showPopNotice */
const openLoginPopNotices = async () => {
  const list = (loginPopNoticeDetails.value || []).filter(
    (el: any) => el && el.id && !isLoginPopNoticeHidden(String(el.id)),
  );

  openLoginPopNoticeItems.value = await Promise.all(
    list.map(async (notice: any, i: number) => {
      const atchFileKey = notice.atchFileKey ?? '';
      const detailUrl = notice.detailUrl ?? '';
      let attachmentFiles: any[] = [];

      if (atchFileKey) {
        try {
          const atchRes = await fetchAtchFileList({ atchFileId: atchFileKey });
          attachmentFiles = atchRes?.fileList ?? [];
        } catch (e) {
          console.error('[popNotice] 첨부파일 조회 실패:', notice.id, e);
        }
      }

      return {
        id: String(notice.id),
        notice,
        atchFileKey,
        detailUrl,
        attachmentFiles,
        open: true,
        doNotShowAgain: false,
        zIndex: 1200 + i,
        offsetIndex: i,
      };
    }),
  );
};

const onDoNotShowLoginPopNoticeChange = (noticeId: string, checked: boolean) => {
  const item = openLoginPopNoticeItems.value.find(x => x.id === String(noticeId));
  if (!item) return;

  item.doNotShowAgain = checked;

  const cookieName = getLoginPopNoticeCookieName(String(noticeId));
  if (checked) {
    const days = Number(item.notice?.popupDay ?? item.notice?.popDay ?? 9999);
    createCookie(cookieName, 'Y', Number.isFinite(days) && days > 0 ? days : 9999, false);
  } else {
    deleteCookie(cookieName);
  }
};

const closeLoginPopNotice = (noticeId: string) => {
  const id = String(noticeId);
  const item = openLoginPopNoticeItems.value.find(x => x.id === id);
  if (!item || !item.open) return;

  item.open = false;
  openLoginPopNoticeItems.value = openLoginPopNoticeItems.value.filter(x => x.open);
};

/** popNoticeList 각 건에 대해 selectDetail 조회 후 팝업 오픈 */
const handleFetchLoginPopNoticeDetails = async () => {
  loginPopNoticeDetails.value = [];

  if (!loginPopNoticeList.value.length) {
    console.log('[popNotice] 조회할 팝업 공지 없음');
    return;
  }

  const details: any[] = [];

  for (const popNotice of loginPopNoticeList.value) {
    try {
      const res = await fetchPopNoticeDetail({ id: popNotice.id });
      // popSelectNoticeDetail → dataVO (기존 selectDetail → data)
      const detail = res?.dataVO ?? res?.data;

      if (detail) {
        details.push({
          ...nonEscapeHTMLObject(detail),
          // width/height 는 checkPopNotice 목록 기준 (상세에 없으면 목록 값 사용)
          width: detail.width ?? popNotice.width,
          height: detail.height ?? popNotice.height,
        });
      }
    } catch (e) {
      console.error('[popNotice] 상세 조회 실패:', popNotice.id, e);
    }
  }

  loginPopNoticeDetails.value = details;

  await openLoginPopNotices();
};

/** 로그인 후 공지사항 팝업 노출 여부 조회 → 목록 상세 조회 → 팝업 */
const handleCheckPopNotice = async () => {
  if (hasCheckedPopNotice.value) {
    return popNoticeCheckResult.value;
  }

  try {
    const res = await fetchCheckPopNotice();

    popNoticeCheckResult.value = res ?? null;
    hasCheckedPopNotice.value = true;

    // AS-IS: gPopNoticeList.push({ id, width, height })
    const list = Array.isArray(res?.popNoticeList) ? res.popNoticeList : [];
    loginPopNoticeList.value = list
      .map((popNotice: any) => ({
        id: String(popNotice?.id ?? ''),
        width: String(popNotice?.width ?? ''),
        height: String(popNotice?.height ?? ''),
      }))
      .filter((item: { id: string }) => !!item.id);

    console.log('[checkPopNotice] popNoticeList:', loginPopNoticeList.value);

    await handleFetchLoginPopNoticeDetails();

    return popNoticeCheckResult.value;
  } catch (e) {
    console.error('[checkPopNotice] API 호출 실패:', e);
    return null;
  }
};

const handleNoticeDetail = async (row: any) => {
  const id = row?.id ?? '';
  if (!id) return;

  try {
    noticeFind.value.id = id;
    const res = await fetchPopNoticeDetail(buildNoticeDetailParams(id));
    noticeDetail.value = nonEscapeHTMLObject(res?.dataVO ?? {});
    isNoticeDetailVisible.value = true;

    const atchFileId = noticeDetail.value?.atchFileKey ?? '';
    if (atchFileId) {
      const atchRes = await fetchAtchFileList({ atchFileId });
      noticeAttachmentFiles.value = atchRes?.fileList ?? [];
    } else {
      noticeAttachmentFiles.value = [];
    }
  } catch (e) {
    console.error(e);
    showAlert(appT('common.error'), appT('common.noticeBoard.detailFetchError'));
  }
};

export const useNoticeBoardStore = () => {
  return {
    isNoticeModalOpen,
    isNoticeDetailVisible,
    noticeList,
    noticeDetail,
    noticeAttachmentFiles,
    popNoticeCheckResult,
    hasCheckedPopNotice,
    loginPopNoticeList,
    loginPopNoticeDetails,
    openLoginPopNoticeItems,
    noticeFind,
    popNotice,
    handleCheckPopNotice,
    handleFetchLoginPopNoticeDetails,
    openLoginPopNotices,
    onDoNotShowLoginPopNoticeChange,
    closeLoginPopNotice,
    handleSelectNoticeList,
    handleNoticeSearch,
    handleNoticeDetail,
    closeNoticeDetail,
  };
};
